import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../../services/authService';
import userManagementService from '../../../services/userManagementService';
import Header from '../../../components/ui/Header';
import FixedSidebar from '../../../components/ui/FixedSidebar';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * Team Management Page for HR Managers
 * Focused interface for managing coordinators and recruiters
 */
const TeamManagementPage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('recruiter');
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    role: 'recruiter',
    password: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get current user
        const user = authService.getStoredUser() || await authService.getCurrentUser();
        setCurrentUser(user);

        // Check if user can manage users
        const canManage = await userManagementService.canManageUsers();
        if (!canManage) {
          throw new Error('You do not have permission to manage team members');
        }

        // Load team data
        await loadTeamData();

      } catch (error) {
        console.error('[TeamManagement] Error initializing:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  const loadTeamData = async () => {
    try {
      // Get all HR users (excluding admins and managers)
      const result = await userManagementService.getHRUsers({ pageSize: 1000 });
      const filteredMembers = result.users?.filter(user => 
        user.role === 'recruiter' || user.role === 'hr_coordinator' || user.role === 'hr_user'
      ) || [];
      
      setTeamMembers(filteredMembers);

      // Calculate team stats
      const stats = {
        total: filteredMembers.length,
        active: filteredMembers.filter(u => u.is_active).length,
        recruiters: filteredMembers.filter(u => u.role === 'recruiter').length,
        coordinators: filteredMembers.filter(u => u.role === 'hr_coordinator').length,
        recentlyAdded: filteredMembers.filter(u => {
          const createdAt = new Date(u.created_at);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return createdAt > weekAgo;
        }).length
      };

      setTeamStats(stats);

    } catch (error) {
      console.error('[TeamManagement] Error loading team data:', error);
      setError(error.message);
    }
  };

  const handleAddMember = async () => {
    try {
      // Validate member data
      const validation = userManagementService.validateUserData(newMember);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return;
      }

      setValidationErrors({});
      
      // Create team member
      await userManagementService.createHRUser(newMember);
      
      // Reset form and close modal
      setNewMember({ name: '', email: '', role: 'recruiter', password: '' });
      setShowAddMemberModal(false);
      
      // Reload team data
      await loadTeamData();
      
    } catch (error) {
      console.error('[TeamManagement] Error adding member:', error);
      setError(error.message);
    }
  };

  const handleUpdateMember = async (memberId, memberData) => {
    try {
      await userManagementService.updateHRUser(memberId, memberData);
      await loadTeamData();
    } catch (error) {
      console.error('[TeamManagement] Error updating member:', error);
      setError(error.message);
    }
  };

  const handleDeactivateMember = async (memberId) => {
    if (!confirm('Are you sure you want to deactivate this team member?')) {
      return;
    }

    try {
      await userManagementService.deleteHRUser(memberId);
      await loadTeamData();
    } catch (error) {
      console.error('[TeamManagement] Error deactivating member:', error);
      setError(error.message);
    }
  };

  const handleResetPassword = async (memberId) => {
    if (!confirm('Are you sure you want to reset this member\'s password?')) {
      return;
    }

    try {
      const result = await userManagementService.resetUserPassword(memberId);
      alert(`Password reset successful. Temporary password: ${result.temporary_password}`);
    } catch (error) {
      console.error('[TeamManagement] Error resetting password:', error);
      setError(error.message);
    }
  };

  const getFilteredMembers = () => {
    if (selectedRole === 'all') return teamMembers;
    return teamMembers.filter(member => member.role === selectedRole);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'recruiter': return 'UserPlus';
      case 'hr_coordinator': return 'Calendar';
      case 'hr_user': return 'User';
      default: return 'User';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'recruiter': return 'bg-green-100 text-green-800';
      case 'hr_coordinator': return 'bg-blue-100 text-blue-800';
      case 'hr_user': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} />
        <FixedSidebar currentPath="/hr-roles/manager" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading team management...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} />
        <FixedSidebar currentPath="/hr-roles/manager" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => navigate('/hr-roles/manager')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} />
      <FixedSidebar currentPath="/hr-roles/manager" />
      
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'HR Roles', href: '/hr-roles' },
            { label: 'Manager Dashboard', href: '/hr-roles/manager' },
            { label: 'Team Management', href: '/team-management', isActive: true }
          ]} />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Team Management</h1>
              <p className="text-muted-foreground">
                Manage your HR team - coordinators, recruiters, and support staff
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={() => navigate('/hr-roles/manager')}
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back to Dashboard
              </Button>
              <Button onClick={() => setShowAddMemberModal(true)}>
                <Icon name="UserPlus" size={16} className="mr-2" />
                Add Team Member
              </Button>
            </div>
          </div>

          {/* Team Stats */}
          {teamStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-blue-600" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Team Members</h3>
                <p className="text-2xl font-bold text-foreground mb-1">{teamStats.total}</p>
                <p className="text-xs text-muted-foreground">Under your management</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icon name="UserCheck" size={24} className="text-green-600" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Active Members</h3>
                <p className="text-2xl font-bold text-foreground mb-1">{teamStats.active}</p>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Icon name="UserPlus" size={24} className="text-purple-600" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Recruiters</h3>
                <p className="text-2xl font-bold text-foreground mb-1">{teamStats.recruiters}</p>
                <p className="text-xs text-muted-foreground">Active recruiters</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Icon name="Calendar" size={24} className="text-orange-600" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Coordinators</h3>
                <p className="text-2xl font-bold text-foreground mb-1">{teamStats.coordinators}</p>
                <p className="text-xs text-muted-foreground">HR coordinators</p>
              </div>
            </div>
          )}

          {/* Role Filter */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setSelectedRole('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedRole === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                All Members ({teamMembers.length})
              </button>
              <button
                onClick={() => setSelectedRole('recruiter')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedRole === 'recruiter'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Recruiters ({teamStats?.recruiters || 0})
              </button>
              <button
                onClick={() => setSelectedRole('hr_coordinator')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedRole === 'hr_coordinator'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Coordinators ({teamStats?.coordinators || 0})
              </button>
            </div>
          </div>

          {/* Team Members Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {getFilteredMembers().map((member) => {
              const displayInfo = userManagementService.getUserDisplayInfo(member);
              return (
                <div key={member.id} className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <img
                      src={displayInfo.avatar}
                      alt={member.name}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{member.name}</h3>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        member.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name={getRoleIcon(member.role)} size={16} className="text-muted-foreground" />
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(member.role)}`}>
                      {displayInfo.roleLabel}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                    <span>Joined: {displayInfo.createdAtFormatted}</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResetPassword(member.id)}
                      className="flex-1"
                    >
                      <Icon name="Key" size={14} className="mr-1" />
                      Reset Password
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeactivateMember(member.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Icon name="UserX" size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {getFilteredMembers().length === 0 && (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No team members found</h3>
              <p className="text-muted-foreground mb-4">
                {selectedRole === 'all' 
                  ? 'Add your first team member to get started'
                  : `No ${selectedRole === 'recruiter' ? 'recruiters' : 'coordinators'} found`
                }
              </p>
              <Button onClick={() => setShowAddMemberModal(true)}>
                <Icon name="UserPlus" size={16} className="mr-2" />
                Add Team Member
              </Button>
            </div>
          )}

          {/* Add Member Modal */}
          {showAddMemberModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-foreground">Add Team Member</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddMemberModal(false);
                      setValidationErrors({});
                      setNewMember({ name: '', email: '', role: 'recruiter', password: '' });
                    }}
                  >
                    <Icon name="X" size={16} />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={newMember.name}
                      onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        validationErrors.name ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Enter team member's full name"
                    />
                    {validationErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={newMember.email}
                      onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        validationErrors.email ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Enter work email address"
                    />
                    {validationErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Role *</label>
                    <select
                      value={newMember.role}
                      onChange={(e) => setNewMember(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="recruiter">Recruiter - Candidate sourcing and interviewing</option>
                      <option value="hr_coordinator">HR Coordinator - Administrative coordination and scheduling</option>
                      <option value="hr_user">HR User - Basic HR access</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Initial Password *</label>
                    <input
                      type="password"
                      value={newMember.password}
                      onChange={(e) => setNewMember(prev => ({ ...prev, password: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        validationErrors.password ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Create initial password"
                    />
                    {validationErrors.password && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Team member can change this password after first login
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddMemberModal(false);
                      setValidationErrors({});
                      setNewMember({ name: '', email: '', role: 'recruiter', password: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddMember}>
                    <Icon name="UserPlus" size={16} className="mr-2" />
                    Add Member
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TeamManagementPage;